import axios from 'axios';
import { createMutation, createQuery } from 'react-query-kit';

import { relativeUrlResolver, restUrl } from '@/services/utils';
import type { PersistedItem } from '@/types/DataModel';

import type { AssetApiRecord } from './assets.types';

const ITEM_SEARCH_COUNT = 20;

const assetItemsUrl = (primaryKey: string, itemPrimaryKey?: string) => relativeUrlResolver(
    `/api/v2/assets/${encodeURIComponent(primaryKey)}/items${itemPrimaryKey ? `/${encodeURIComponent(itemPrimaryKey)}` : ''}`,
);

/** Full item records for the asset's linked item keys, for the Linked Articles table. */
const useLinkedItemRecords = createQuery<PersistedItem[], { primaryKeys: string[] }, Error>({
    queryKey: ['assets', 'linked-item-records'],
    fetcher: async ({ primaryKeys }) => {
        const response = await axios.post(restUrl('items/findByPrimaryKeys'), { items: primaryKeys, fields: [] });
        return response.data ?? [];
    },
});

const searchItems = async ({ keyword }: { keyword: string }): Promise<PersistedItem[]> => {
    const response = await axios.get(restUrl('items'), { params: { keyword, count: ITEM_SEARCH_COUNT } });
    return response.data ?? [];
};

const useItemSearch = createQuery<PersistedItem[], { keyword: string }, Error>({
    queryKey: ['assets', 'item-search'],
    fetcher: searchItems,
});

const linkItem = async ({ primaryKey, itemPrimaryKey }: { primaryKey: string; itemPrimaryKey: string }): Promise<AssetApiRecord> => {
    const response = await axios.post(assetItemsUrl(primaryKey), { itemPrimaryKey });
    return response.data;
};

const useLinkItem = createMutation<AssetApiRecord, { primaryKey: string; itemPrimaryKey: string }, Error>({
    mutationFn: linkItem,
});

const useUnlinkItem = createMutation<AssetApiRecord, { primaryKey: string; itemPrimaryKey: string }, Error>({
    mutationFn: async ({ primaryKey, itemPrimaryKey }) => {
        const response = await axios.delete(assetItemsUrl(primaryKey, itemPrimaryKey));
        return response.data;
    },
});

export { linkItem, searchItems, useItemSearch, useLinkedItemRecords, useLinkItem, useUnlinkItem };
